/**
 * Stock rules as the panels show them, over the analysis inventory module.
 * The badges in the prediction card, the graph box and the report use the
 * part's own minStock (uiStockStatus); the manager status uses the fixed
 * CONSTANTS thresholds (critical 1, low 2) and ON_ORDER (getStockStatus).
 */
import { getStockStatus, uiStockStatus, type StockStatus } from '@/features/twin/analysis';
import type { Tone } from '@/components/ui/primitives';

export type { StockStatus };

export interface StockLike {
  quantity: number;
  minStock?: number;
  onOrder?: boolean;
}

export type StockBadgeLabel = 'IN STOCK' | 'LOW STOCK' | 'OUT OF STOCK';

const BADGE_TONE: Record<'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK', Tone> = { IN_STOCK: 'nominal', LOW_STOCK: 'caution', OUT_OF_STOCK: 'warning' };

/** Badge rule: 0 is out, at or below minStock (default 1) is low, else in stock. */
export function stockBadge(part: StockLike): { label: StockBadgeLabel; tone: Tone } {
  const status = uiStockStatus(part.quantity, part.minStock ?? 1);
  return { label: status.replace(/_/g, ' ') as StockBadgeLabel, tone: BADGE_TONE[status] };
}

/** Manager rule: 0 is ON_ORDER when onOrder else OUT_OF_STOCK; at or below the fixed low threshold is LOW_STOCK. */
export function stockStatus(part: StockLike): StockStatus {
  return getStockStatus(part);
}

export function stockStatusTone(status: StockStatus): Tone {
  if (status === 'IN_STOCK') return 'nominal';
  if (status === 'LOW_STOCK') return 'caution';
  if (status === 'ON_ORDER') return 'live';
  return 'warning';
}

export function stockStatusLabel(status: StockStatus): string {
  return status.replace(/_/g, ' ');
}
